import React, {
  ReactElement,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import { useHistory } from 'react-router-dom';

import useAccountsColumns from './useAccountsColumns';
import { checkBoxList } from '../CopyAccount/checkBoxList';

import { Stack, Grid, Checkbox, Button } from 'ui/components';
import {
  SlidePanel,
  Alert,
  SearchInput,
  Card,
  CardHeader,
  CardBody,
  Table,
  EmptyState,
  Loader,
} from 'ui/patterns';

import {
  useGetAccounts,
  useGetPublishedProcedures,
  useSaveImportAccount,
  useGetUserSettings,
  useGetFtpSettings,
} from 'hooks';

import NoDataImage from 'assets/images/NoDataImage';
import * as Icon from 'assets/icons';

import { confirmAlert } from 'utils/confirm-alert';
import { showErrorToast, showSuccessToast } from 'utils/showToast';

import { ImportAccountToggles } from './importAccountTypes';
import { ResultModal, ErrorModal } from 'types/modalTypes';
import {
  ImportAccountProps,
  CopiedDataType,
  CopiedDataItemType,
} from './importAccountTypes';
import { PublishedProcedureType } from 'types/procedureBuilder';

const sortItem = { sortKey: 'Phone Display', sortOrder: 'asc' };

export default function ImportAccount({
  showPanel,
  togglePanel,
  accountName,
  importAccountId,
  account,
}: ImportAccountProps): ReactElement {
  const [columnsSearch, setColumnsSearch] = useState({}); // header search
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);
  const [keywordSearch, setKeywordSearch] = useState(''); // keyword search
  const [accountId, setAccountId] = useState(0); // selected account id from table
  const [selectedProcedure, setSelectedProcedure] =
    useState<PublishedProcedureType>([]);
  const [procedures, setProcedures] = useState<
    PublishedProcedureType | undefined
  >([]);
  const [selectedCopiedData, setSelectedCopiedData] = useState<CopiedDataType>(
    [],
  );
  const [copiedData, setCopiedData] = useState<CopiedDataType>([
    ...checkBoxList,
  ]);
  const [loadingText, setLoadingText] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>();

  const tbodyRef = useRef() as React.MutableRefObject<HTMLTableSectionElement>;

  let interval: ReturnType<typeof setTimeout>;

  const accountStatus = 'Live';

  const history = useHistory<{ ID: number }>();

  const {
    data: accountList,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useGetAccounts(
    accountStatus,
    keywordSearch,
    columnsSearch,
    currentSortItem,
  );

  const { data: userSettings } = useGetUserSettings();

  const columns = useAccountsColumns(userSettings);

  const { data: publishedProcedures, isLoading: isProcedureLoading } =
    useGetPublishedProcedures(accountId);

  const { mutate: saveImportAccount, isLoading: isSaveImportAccountLoading } =
    useSaveImportAccount();

  const { data: ftpInfo } = useGetFtpSettings(accountId);

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (
      publishedProcedures &&
      publishedProcedures.Data &&
      publishedProcedures.Data.length > 0
    )
      setProcedures([...publishedProcedures.Data]);
    else setProcedures([]);
  }, [publishedProcedures]);

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const tableData = useMemo(() => {
    if (accountList) {
      return accountList.pages.map((dataItem: any) => dataItem.Data).flat();
    }
  }, [accountList]);

  const totalItems = useMemo(() => {
    if (accountList) {
      return accountList.pages[0].FilteredRecords;
    }
    return 0;
  }, [accountList]);

  const handleKeywordSearch = (value: string): void => {
    setKeywordSearch(value);
  };

  const handleReset = (): void => {
    setKeywordSearch('');
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
    setAccountId(0);
  };

  const handleRowClick = (row: any): void => {
    if (row.original.Id === importAccountId) {
      confirmAlert({
        message: 'Account cannot be imported to the same account.',
        buttons: [
          {
            label: 'Ok',
            onClick: () => {
              //
            },
            className: 'pr-btn-primary',
          },
        ],
      });
    } else {
      setSelectedProcedure([]);
      setSelectedCopiedData([]);
      setAccountId(row.original.Id);
    }
  };

  const onEndReached = () => {
    if (tableData) {
      if (hasNextPage && tableData?.length < totalItems) fetchNextPage();
    }
  };

  const isDataToCopyChecked = (id: number) => {
    const item =
      selectedCopiedData && selectedCopiedData.find(item => item.id === id);
    if (item) {
      return true;
    } else return false;
  };

  const handleSelectDataToCopy = (dataItem: CopiedDataItemType) => {
    const index =
      selectedCopiedData && selectedCopiedData.length > 0
        ? selectedCopiedData.findIndex(item => item.id === dataItem.id)
        : -1;
    if (index === -1) {
      selectedCopiedData &&
        selectedCopiedData.push({
          id: dataItem.id,
          label: dataItem.label,
          name: dataItem.name,
        });
    } else if (index >= 0) {
      selectedCopiedData && selectedCopiedData.splice(index, 1);
    }

    if (selectedCopiedData) setSelectedCopiedData([...selectedCopiedData]);
  };

  const isCheckedAllData = () => {
    if (
      selectedCopiedData?.length ===
      (copiedData && copiedData.length > 0 && copiedData.length)
    )
      return true;
    else return false;
  };

  const handleSelectAllData = () => {
    let allDatas: CopiedDataItemType[] = [];
    const isSelect = isCheckedAllData();
    if (!isSelect) {
      allDatas =
        copiedData && copiedData.length > 0
          ? copiedData.map(item => {
              return {
                id: item.id,
                label: item.label,
                name: item.name,
              };
            })
          : [];
    } else {
      allDatas = [];
    }
    setSelectedCopiedData([...allDatas]);
  };

  const isProcedureChecked = (id: number) => {
    const item =
      selectedProcedure && selectedProcedure.find(item => item.Id === id);
    if (item) {
      return true;
    } else return false;
  };

  const handleSelectProcedure = (procItem: { Id: number; Name: string }) => {
    const index =
      selectedProcedure && selectedProcedure.length > 0
        ? selectedProcedure.findIndex(item => item.Id === procItem.Id)
        : -1;
    if (index === -1) {
      selectedProcedure &&
        selectedProcedure.push({
          Id: procItem.Id,
          Name: procItem.Name,
        });
    } else if (index >= 0) {
      selectedProcedure && selectedProcedure.splice(index, 1);
    }

    if (selectedProcedure) setSelectedProcedure([...selectedProcedure]);
  };

  const isCheckedAllProcedures = () => {
    if (
      selectedProcedure?.length ===
      (procedures && procedures.length > 0 && procedures.length)
    )
      return true;
    else return false;
  };

  const handleSelectAllProcedures = () => {
    let allprocedures: PublishedProcedureType = [];
    const isSelect = isCheckedAllProcedures();
    if (!isSelect) {
      allprocedures =
        procedures && procedures.length > 0
          ? procedures.map(item => {
              return {
                Id: item.Id,
                Name: item.Name,
              };
            })
          : [];
    } else {
      allprocedures = [];
    }
    if (allprocedures) setSelectedProcedure([...allprocedures]);
  };

  const handleProcedureSearch = (value: string) => {
    let newProcedures: PublishedProcedureType | undefined = [];
    newProcedures =
      publishedProcedures &&
      publishedProcedures.Data &&
      publishedProcedures.Data.filter((item: any) => {
        if (item.Name.toLowerCase().includes(value.toLowerCase())) return item;
        return false;
      });

    if (newProcedures) setProcedures([...newProcedures]);
    if (newProcedures?.length === 0) setSelectedProcedure([...newProcedures]);
    setSearchValue(value);
  };

  function resetForm() {
    setSelectedProcedure([]);
    publishedProcedures && setProcedures(publishedProcedures.Data);
    setSelectedCopiedData([]);
    setCopiedData(checkBoxList);
    handleReset();
  }

  const startInterval = () => {
    let index = 0;
    interval = setInterval(() => {
      if (index < selectedCopiedData.length) {
        setLoadingText(`Copying ${selectedCopiedData[index].label}`);
      } else if (
        selectedProcedure &&
        selectedProcedure.length > 0 &&
        loadingText !== 'Finalizing Account Copy'
      ) {
        setLoadingText('Copying Procedures');
      } else {
        setLoadingText('Finalizing Account Copy');
      }
      index = index + 1;
    }, 500);
  };

  const importData = () => {
    const procedureList =
      (selectedProcedure && selectedProcedure.map(item => item.Id)) || [];

    const initialList: ImportAccountToggles = {
      CopyOfficeFacts: false,
      CopyLocationList: false,
      CopyContactList: false,
      CopyProviderList: false,
      CopyCustomTablesAndForms: false,
      CopyTagMappings: false,
      CopyFtpSettings: false,
    };

    if (
      selectedCopiedData.findIndex(
        selectedItem => selectedItem.name === 'CopyOfficeFacts',
      ) >= 0
    ) {
      initialList.CopyOfficeFacts = true;
    }
    if (
      selectedCopiedData.findIndex(
        selectedItem => selectedItem.name === 'CopyLocationList',
      ) >= 0
    ) {
      initialList.CopyLocationList = true;
    }

    if (
      selectedCopiedData.findIndex(
        selectedItem => selectedItem.name === 'CopyContactList',
      ) >= 0
    ) {
      initialList.CopyContactList = true;
    }
    if (
      selectedCopiedData.findIndex(
        selectedItem => selectedItem.name === 'CopyCustomTablesAndForms',
      ) >= 0
    ) {
      initialList.CopyCustomTablesAndForms = true;
    }
    if (
      selectedCopiedData.findIndex(
        selectedItem => selectedItem.name === 'CopyTagMappings',
      ) >= 0
    ) {
      initialList.CopyTagMappings = true;
    }
    if (
      selectedCopiedData.findIndex(
        selectedItem => selectedItem.name === 'CopyFtpSettings',
      ) >= 0
    ) {
      initialList.CopyFtpSettings = true;
    }

    startInterval();
    if (account !== undefined) {
      saveImportAccount(
        {
          params: {
            ...initialList,
            ...account,
            ProcedureIds: procedureList,
            AccountId: accountId,
          },
          currentAccount: importAccountId,
        },
        {
          onSuccess: (mutationResult: ResultModal) => {
            clearInterval(interval);
            setLoadingText('Finalizing Account Import');
            setTimeout(() => {
              setLoadingText('');
              history.replace(
                `/home/accounts/edit/${mutationResult?.Data?.Id}/accountInfo`,
                { ID: mutationResult?.Data?.Id },
              );
              togglePanel();
              resetForm();
              showSuccessToast({
                message: 'Your changes were successfully saved.',
                autoClose: 3000,
              });
            }, 500);
          },
          onError: (e: ErrorModal) => {
            clearInterval(interval);
            setLoadingText('');
            showErrorToast({ message: e.Errors[0].Message, autoClose: 10000 });
          },
        },
      );
    }
  };

  function handleSubmit() {
    const ftpChecked = selectedCopiedData.find(
      item => item.label === 'FTP Settings',
    );
    if (ftpChecked && ftpInfo?.IsEnable) {
      confirmAlert({
        buttonOrder: true,
        message:
          'WARNING: This process will copy SFTP settings to the new account. All documents finalized in the new account will send to the same server. Are you sure you want to include SFTP Settings in this account copy process?',
        buttons: [
          {
            label: 'Cancel',
            onClick: () => {
              //
            },
            className: 'pr-btn-secondary',
          },
          {
            label: 'No',
            onClick: () => {
              const selected = copiedData.find(item => item.id === 6);
              if (selected) handleSelectDataToCopy(selected);
              importData();
            },
          },
          {
            label: 'Yes',
            onClick: () => {
              importData();
            },
            className: 'pr-btn-primary',
          },
        ],
      });
    } else {
      importData();
    }
  }

  const importAccountActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel();
          resetForm();
        }}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        variant="primary"
        onClick={handleSubmit}
        disabled={
          accountId &&
          ((selectedCopiedData && selectedCopiedData?.length > 0) ||
            (selectedProcedure && selectedProcedure?.length > 0))
            ? false
            : true
        }
      >
        Save
      </Button>
    </>
  );

  return (
    <>
      <SlidePanel
        title={`Import Account Data to - ${accountName}`}
        actions={importAccountActions()}
        onClose={() => {
          togglePanel();
          resetForm();
        }}
        open={showPanel}
        size="lg"
      >
        <Stack direction="y" gap={6}>
          <Alert variant="warning" icon={<Icon.Warning />}>
            <strong>Warning!</strong> When importing data, existing data in
            selected areas will be overwritten.
          </Alert>

          <Box>
            <Flex justifyContent="space-between" alignItems="center" mb={2}>
              <Text fontSize={2} color="text.body" fontWeight="medium">
                Select Account to Import From
              </Text>
              <Stack direction="x" gap={2}>
                <SearchInput
                  placeholder="Search Accounts"
                  value={keywordSearch}
                  handleChange={handleKeywordSearch}
                />
                <Button
                  iconBefore={<Icon.Refresh title="" ariaHidden="true" />}
                  variant="secondary"
                  onClick={handleReset}
                >
                  Reset All
                </Button>
              </Stack>
            </Flex>

            <Table
              columns={columns}
              data={tableData}
              isLoading={isLoading}
              headerUpdateCallBack={headerUpdateCallBack}
              handleSortChange={handleSortChange}
              onRowClick={handleRowClick}
              sortedItem={currentSortItem}
              columnsSearch={columnsSearch}
              height="350px"
              noOptions
              selectedRowId={accountId}
              onEndReached={onEndReached}
              isFetching={!!isFetchingNextPage}
            />

            <Flex mt={4} justifyContent="space-between" alignItems="center">
              <Text fontSize={1} color="text.body" sx={{ height: '20px' }}>
                {tableData && tableData.length > 0 && (
                  <Text mb={2}>{`Showing ${tableData.length} of ${totalItems}${
                    isFetching ? ' | Processing' : ''
                  }`}</Text>
                )}
              </Text>
            </Flex>
          </Box>
          <Card>
            <CardHeader px={3} py={2}>
              <Flex justifyContent="space-between" alignItems="center">
                <Text
                  as="h5"
                  fontSize={2}
                  color="text.body"
                  fontWeight="medium"
                >
                  Data to Import
                </Text>
                <Checkbox
                  label="Select All"
                  name="allDatas"
                  checked={isCheckedAllData()}
                  handleChange={handleSelectAllData}
                />
              </Flex>
            </CardHeader>
            <CardBody p={3}>
              <Grid minColWidth="200px" gap={4}>
                {checkBoxList &&
                  checkBoxList.map(dataItem => (
                    <Checkbox
                      key={dataItem.id}
                      label={dataItem.label}
                      name={dataItem.label}
                      checked={isDataToCopyChecked(dataItem.id)}
                      handleChange={() => handleSelectDataToCopy(dataItem)}
                    />
                  ))}
              </Grid>
            </CardBody>
          </Card>
          <Card>
            <CardHeader px={3} py={2}>
              <Flex justifyContent="space-between" alignItems="center">
                <Text
                  as="h5"
                  fontSize={2}
                  color="text.body"
                  fontWeight="medium"
                >
                  Select Procedures to Import to this Account{' '}
                  <Text as="small" fontWeight="regular">
                    {`(You have selected ${selectedProcedure?.length} out of ${
                      procedures?.length || 0
                    } procedures)`}
                  </Text>
                </Text>
                <Stack direction="x" gap={3}>
                  <SearchInput
                    placeholder="Search Procedures"
                    value={searchValue}
                    handleChange={handleProcedureSearch}
                  />
                  <Checkbox
                    label="Select All"
                    name="allProcedures"
                    checked={isCheckedAllProcedures()}
                    handleChange={handleSelectAllProcedures}
                  />
                </Stack>
              </Flex>
            </CardHeader>
            <CardBody p={3} minHeight="200px" style={{ position: 'relative' }}>
              <Grid minColWidth="220px" gap={4}>
                {isProcedureLoading ? (
                  <Loader />
                ) : procedures && procedures.length > 0 ? (
                  procedures.map(procItem => (
                    <Checkbox
                      key={procItem.Id}
                      label={procItem.Name}
                      name={procItem.Name}
                      checked={isProcedureChecked(procItem.Id)}
                      handleChange={() => handleSelectProcedure(procItem)}
                    />
                  ))
                ) : (
                  <EmptyState>
                    <NoDataImage feedback="No data available" />
                  </EmptyState>
                )}
              </Grid>
            </CardBody>
          </Card>
        </Stack>
      </SlidePanel>
      {(isSaveImportAccountLoading || loadingText) && (
        <Loader text={`${loadingText}`} />
      )}
    </>
  );
}
